/**
 * PublicPartnerMenu — Phase 101ep (Feb 2026)
 *
 * Page publique consultable/imprimable par un hébergeur partenaire (ex :
 * Sion Piscine) pour visualiser le catalogue Maison Thaï et proposer une
 * rotation de plats sur son distributeur.
 *
 * Différences avec `/corporate/:id` :
 *  • Ne dépend PAS du flag `is_corporate` du distributeur.
 *  • Affiche le prix PUBLIC consommateur (`price_direct`), sans prix négocié.
 *  • Union des plats en rail (« Sur machine ») + plats suggérables actifs.
 *
 * URL : /partenaire/:id  (id = UUID du distributeur, non guessable)
 * API : GET /api/public/partner/:id/menu  (pas d'authentification)
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Loader2, Package2 } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";

const fmtCHF = (n) => `${Number(n || 0).toFixed(2)} CHF`;

export default function PublicPartnerMenu() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("machine"); // "machine" | "all"

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(`/public/partner/${id}/menu`);
        setData(data);
      } catch (e) {
        setError(e?.response?.status === 404 ? "Catalogue introuvable" : "Erreur de chargement");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const filtered = useMemo(() => {
    if (!data?.items) return [];
    if (tab === "machine") return data.items.filter((it) => it.is_current_on_machine);
    return data.items;
  }, [data, tab]);

  const grouped = useMemo(() => {
    const map = {};
    for (const it of filtered) {
      const key = it.category || "Autres";
      if (!map[key]) map[key] = [];
      map[key].push(it);
    }
    return map;
  }, [filtered]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF4E9]">
        <Loader2 className="w-8 h-8 animate-spin text-[#8C6B2F]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF4E9]">
        <div className="text-center" data-testid="partner-menu-error">
          <div className="text-2xl font-semibold text-[#7A1F1F] mb-2">{error}</div>
          <p className="text-sm text-[#575A57]">Vérifiez le lien fourni ou contactez Maison Thaï.</p>
        </div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="min-h-screen bg-[#FBF4E9] py-8 print:py-0 print:bg-white">
      <style>{`
        @media print {
          @page { margin: 15mm; }
          .no-print { display: none !important; }
          .print-page { max-width: 100% !important; box-shadow: none !important; }
        }
      `}</style>

      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden print-page">
        {/* En-tête */}
        <div className="px-8 py-6 border-b-2 border-[#8C6B2F] flex items-center justify-between">
          <div>
            <img
              src="/company-name-sayonara.png"
              alt="Maison Thaï"
              className="h-8 mb-2"
            />
            <div className="text-xs text-[#575A57]">
              Catalogue partenaire · {data.distributor_name}
              {data.distributor_city ? ` · ${data.distributor_city}` : ""}
            </div>
          </div>
          <Button
            onClick={() => window.print()}
            className="bg-[#8C6B2F] hover:bg-[#735524] text-white no-print"
            data-testid="print-partner-menu"
          >
            <Printer className="w-4 h-4 mr-2" /> Imprimer
          </Button>
        </div>

        {/* Introduction + filtre */}
        <div className="px-8 py-4 bg-[#FAFAF7] border-b border-[#E5E5E0]">
          <p className="text-sm text-[#1A1C1A] mb-3">
            Voici le catalogue Maison Thaï disponible pour votre distributeur.
            Utilisez ce document pour proposer une rotation de plats — contactez
            votre référent Maison Thaï pour valider les changements.
          </p>
          {data.restricted_to_machine && (
            <p className="text-[11px] text-[#8C6B2F] italic mb-3 bg-[#FBF4E9] border-l-2 border-[#8C6B2F] pl-2 py-1">
              Ce catalogue est filtré pour votre machine : seuls les produits
              physiquement compatibles avec votre distributeur sont affichés.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 no-print">
            <button
              onClick={() => setTab("machine")}
              className={`text-xs uppercase tracking-wider font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                tab === "machine"
                  ? "bg-[#1F4E3D] text-white border-[#1F4E3D]"
                  : "bg-white text-[#1F4E3D] border-[#A8C9B6] hover:bg-[#E8F2EC]"
              }`}
              data-testid="tab-on-machine"
            >
              Sur ma machine ({data.count_installed || 0})
            </button>
            <button
              onClick={() => setTab("all")}
              className={`text-xs uppercase tracking-wider font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                tab === "all"
                  ? "bg-[#8C6B2F] text-white border-[#8C6B2F]"
                  : "bg-white text-[#8C6B2F] border-[#E8D3A8] hover:bg-[#FBF4E9]"
              }`}
              data-testid="tab-all-catalog"
            >
              Catalogue complet ({data.count_total || 0})
            </button>
          </div>
        </div>

        {/* Liste produits */}
        <div className="px-8 py-6">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-base font-semibold text-[#1A1C1A] uppercase tracking-wide flex items-center gap-2">
              <Package2 className="w-4 h-4 text-[#8C6B2F]" />
              {tab === "machine" ? "Plats actuellement disponibles" : "Catalogue Maison Thaï"}
            </h2>
            <span className="text-xs text-[#575A57]" data-testid="partner-menu-counter">
              <span className="font-semibold text-[#1F4E3D]">{data.count_installed || 0}</span>
              {" "}sur machine · {data.count_total || 0} au catalogue
            </span>
          </div>

          {filtered.length === 0 ? (
            <div className="text-sm text-[#575A57] italic py-6 text-center border border-dashed border-[#E5E5E0] rounded">
              Aucun plat à afficher.
            </div>
          ) : (
            Object.entries(grouped).map(([category, items]) => (
              <div key={category} className="mb-5" data-testid={`partner-category-${category}`}>
                {category && category !== "Autres" && (
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#8C6B2F] mb-1.5">
                    {category}
                  </div>
                )}
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="text-xs text-[#575A57] border-b border-[#E5E5E0]">
                      <th className="text-left py-1.5 font-medium">Produit</th>
                      <th className="text-right py-1.5 font-medium w-32">Prix public</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr
                        key={it.product_id}
                        className="border-b border-[#F0F0EB]"
                        data-testid={`partner-item-${it.product_id}`}
                      >
                        <td className="py-1.5">
                          <div className="font-medium text-[#1A1C1A] flex items-center gap-1.5 flex-wrap">
                            {it.name}
                            {it.is_current_on_machine ? (
                              <span
                                className="inline-block text-[9px] uppercase tracking-wider text-[#1F4E3D] bg-[#E8F2EC] border border-[#A8C9B6] rounded px-1.5 py-0.5 font-semibold"
                                title={it.slot ? `Rail ${it.slot}` : "Actuellement chargé sur votre machine"}
                              >
                                Sur machine{it.slot ? ` · ${it.slot}` : ""}
                              </span>
                            ) : (
                              <span
                                className="inline-block text-[9px] uppercase tracking-wider text-[#8C6B2F] bg-[#FBF4E9] border border-[#E8D3A8] rounded px-1.5 py-0.5 font-semibold"
                                title="Peut être demandé en réassort"
                              >
                                Suggérable
                              </span>
                            )}
                          </div>
                          {it.portion_size && (
                            <div className="text-[10px] text-[#A3A6A3]">{it.portion_size} g</div>
                          )}
                        </td>
                        <td className="py-1.5 text-right font-mono font-semibold text-[#1A1C1A]">
                          {fmtCHF(it.price_public)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}

          <p className="text-[10px] text-[#A3A6A3] italic mt-3">
            Prix indiqués = tarif public consommateur affiché sur la machine.
            Pour proposer une rotation de plats, transmettez ce document à
            votre référent Maison Thaï.
          </p>
        </div>

        <div className="px-8 py-4 text-[10px] text-[#A3A6A3] text-center border-t border-[#E5E5E0]">
          Document généré le {new Date(data.generated_at).toLocaleString("fr-CH")}
          {" · "}
          Maison Thaï · Pongsak Phimrat · CH-1964 Conthey · TVA CHE-291.717.594
        </div>
      </div>
    </div>
  );
}
